import type { ReactNode } from 'react'
import { AlertTriangle, Inbox, Lock, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { Button } from './ui/button'

/**
 * SKILL §1 tamoyil 5: yuklanish / bo'sh / xato / muvaffaqiyat — TO'RTTASI HAM
 * dizayn qilinadi. «Hech narsa yo'q» ekran TAQIQLANADI.
 */

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode
  title?: ReactNode
  description?: ReactNode
  /** SKILL §6: bo'sh holatda KEYINGI QADAM taklif qilinadi. */
  action?: ReactNode
  className?: string
}) {
  const t = useT()
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-16 text-center', className)}>
      <div className="text-tertiary" aria-hidden>
        {icon ?? <Inbox size={28} strokeWidth={1.5} />}
      </div>
      <div className="space-y-1">
        <p className="text-md font-medium text-primary">{title ?? t.states.emptyTitle}</p>
        <p className="max-w-sm text-sm text-secondary">{description ?? t.states.emptyDesc}</p>
      </div>
      {action}
    </div>
  )
}

export function ErrorState({
  title,
  description,
  code,
  onRetry,
  className,
}: {
  title?: ReactNode
  description?: ReactNode
  /** Xato kodi — support'ga aytish uchun ko'rsatiladi. */
  code?: string
  onRetry?: () => void
  className?: string
}) {
  const t = useT()
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-16 text-center', className)}>
      <AlertTriangle size={28} strokeWidth={1.5} className="text-danger" aria-hidden />
      <div className="space-y-1">
        <p className="text-md font-medium text-primary">{title ?? t.states.errorTitle}</p>
        <p className="max-w-sm text-sm text-secondary">{description ?? t.states.errorDesc}</p>
        {code && <p className="pt-1 font-mono text-xs text-tertiary">{code}</p>}
      </div>
      {onRetry && (
        <Button onClick={onRetry}>
          <RefreshCw size={13} />
          {t.common.retry}
        </Button>
      )}
    </div>
  )
}

/** SKILL §6: skeleton qatorlar (spinner EMAS), yakuniy layout o'lchamida. */
export function TableSkeleton({ rows = 12, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-busy="true" className="divide-y divide-border-subtle">
      <span className="sr-only">…</span>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 px-3" style={{ height: 'var(--row-height)' }}>
          {Array.from({ length: columns }, (_, c) => (
            <div
              key={c}
              className="h-3 animate-pulse rounded-sm bg-surface-3"
              style={{
                // Turli kenglik — haqiqiy jadval ritmini beradi.
                width: `${[18, 26, 14, 22, 12, 16][c % 6]}%`,
                animationDelay: `${(r % 4) * 90}ms`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-sm bg-surface-3', className)} aria-hidden />
}

/**
 * rbac-ui.md: huquqsiz URL'ga o'tilsa — QAYSI huquq kerakligi aytiladi
 * va «So'rash» tugmasi ko'rsatiladi.
 */
export function PermissionDenied({ permission, onRequest }: { permission: string; onRequest?: () => void }) {
  const t = useT()
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
      <Lock size={28} strokeWidth={1.5} className="text-tertiary" aria-hidden />
      <div className="space-y-1">
        <p className="text-md font-medium text-primary">{t.states.permissionDeniedTitle}</p>
        <p className="max-w-md text-sm text-secondary">{t.states.permissionDeniedDesc(permission)}</p>
      </div>
      {onRequest && <Button onClick={onRequest}>{t.states.requestAccess}</Button>}
    </div>
  )
}
